/**
 * DOM Manipulation Utilities
 * 
 * Helper functions for DOM manipulation, text positioning, and layout management
 * extracted from Note.js component to improve modularity and reusability.
 */

/**
 * Calculate cursor position in the textarea for dropdown placement
 * Uses a hidden mirror div to accurately calculate text dimensions, taking
 * into account line wrapping and text wrapping.
 *
 * @param {number} triggerIndex - Index of the trigger character (@ or #)
 * @param {React.RefObject} textAreaRef - Reference to the textarea element
 * @param {string} textContent - The current text content of the textarea
 * @returns {Object} - An anchor element with proper getBoundingClientRect method
 */
export const getAnchorPosition = (triggerIndex, textAreaRef, textContent) => {
  if (!textAreaRef.current) return null;

  const textArea = textAreaRef.current;
  const textAreaRect = textArea.getBoundingClientRect();
  const textAreaStyles = window.getComputedStyle(textArea);
  
  // Create a hidden div that mirrors the textarea exactly
  const mirror = createMirrorElement(textAreaStyles);

  // Add mirror to DOM temporarily
  document.body.appendChild(mirror);
  
  try {
    // Get text up to trigger position
    const textToTrigger = textContent.substring(0, triggerIndex);
    
    // Set the text content in mirror
    mirror.textContent = textToTrigger;
    
    // Create a span element to measure the exact position
    const span = document.createElement('span');
    span.textContent = '|'; // Use a character to measure position
    mirror.appendChild(span);
    
    // Get the position of the span (which represents our cursor)
    const spanRect = span.getBoundingClientRect();
    
    // Calculate position relative to textarea, accounting for scroll
    const scrollTop = textArea.scrollTop;
    const scrollLeft = textArea.scrollLeft;
    
    // Get padding values
    const paddingTop = parseFloat(textAreaStyles.paddingTop) || 0;
    const paddingLeft = parseFloat(textAreaStyles.paddingLeft) || 0;
    const borderTop = parseFloat(textAreaStyles.borderTopWidth) || 0;
    const borderLeft = parseFloat(textAreaStyles.borderLeftWidth) || 0;
    
    // Calculate the mirror position relative to its container
    const mirrorRect = mirror.getBoundingClientRect();
    const spanOffsetTop = spanRect.top - mirrorRect.top;
    const spanOffsetLeft = spanRect.left - mirrorRect.left;
    
    // Calculate absolute position for the dropdown
    const absoluteTop = textAreaRect.top + borderTop + paddingTop + spanOffsetTop - scrollTop;
    const absoluteLeft = textAreaRect.left + borderLeft + paddingLeft + spanOffsetLeft - scrollLeft;
    
    // Create virtual anchor
    return {
      getBoundingClientRect: () => ({
        top: absoluteTop,
        left: absoluteLeft,
        right: absoluteLeft,
        bottom: absoluteTop + parseFloat(textAreaStyles.lineHeight || '20'),
        width: 0,
        height: parseFloat(textAreaStyles.lineHeight || '20'),
      }),
    };
  } finally {
    // Always clean up the mirror element
    document.body.removeChild(mirror);
  }
};

/**
 * Create a mirror element that accurately represents the textarea's styles
 *
 * @param {Object} textAreaStyles - Computed styles of the textarea
 * @returns {HTMLElement} - The mirror element
*/
export const createMirrorElement = (textAreaStyles) => {
  const mirror = document.createElement('div');

  // Set base styles with Object.assign for clarity
  Object.assign(mirror.style, {
    position: 'absolute',
    visibility: 'hidden',
    height: 'auto',
    left: '-9999px',
    top: '-9999px',
    overflow: 'hidden',
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap'
  });

  // Copy computed styles efficiently
  const stylesToCopy = [
    'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing',
    'textTransform', 'wordSpacing', 'textIndent', 'textAlign',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'borderStyle', 'boxSizing', 'lineHeight', 'width', 'minHeight', 'maxHeight'
  ];

  stylesToCopy.forEach(prop => {
    mirror.style[prop] = textAreaStyles[prop];
  });

  return mirror;
};

/**
 * Update cursor position and focus
 * Consolidated function to handle cursor positioning across the component
 *
 * @param {React.RefObject} textAreaRef - Reference to the textarea element
 * @param {number} newPos - New cursor position in the textarea
 */
export const updateCursorPosition = (textAreaRef, newPos) => {
  // Using requestAnimationFrame for better performance
  requestAnimationFrame(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.selectionStart = newPos;
      textAreaRef.current.selectionEnd = newPos;
    }
  });
};

/**
 * Get DOM elements needed for resizing and layout adjustments
 * Returns references to the main UI components for manipulation
 *
 * @param {string} contentAreaSelector - Selector for the note content area
 * @param {string} inputCardSelector - Selector for the note input card
 * @param {string} referencesCardSelector - Selector for the references card
 * @param {string} resizeHandleSelector - Selector for the resize handle
 * @returns {Object} Object containing references to DOM elements
 */
export const getElements = (
  contentAreaSelector = '.note-content-area',
  inputCardSelector = '.note-input-card',
  referencesCardSelector = '.references-card',
  resizeHandleSelector = '.resize-handle'
) => {
  const noteContentArea = document.querySelector(contentAreaSelector);
  const noteInputCard = document.querySelector(inputCardSelector);
  const referencesCard = document.querySelector(referencesCardSelector);
  const resizeHandle = document.querySelector(resizeHandleSelector);
  
  return { noteContentArea, noteInputCard, referencesCard, resizeHandle };
};
  
/**
 * Adjust text area height based on references panel height
 * Calculates and sets the appropriate height for the note input area
 * to ensure it fills the available space
 *
 * @param {HTMLElement} noteContentArea - The container element for the note interface
 * @param {HTMLElement} noteInputCard - The text area container element
 * @param {number} referencesHeight - Current height of the references panel in pixels
 * @param {Object} options - Additional options like padding and handle height
 */
export const adjustTextAreaHeight = (noteContentArea, noteInputCard, referencesHeight, options = {}) => {
  const padding = options.padding || 16; // Bottom padding
  const handleHeight = options.handleHeight || 6; // Resize handle height
  const availableHeight = noteContentArea.offsetHeight - referencesHeight - handleHeight - padding;
  noteInputCard.style.height = `${availableHeight}px`;
};

/**
 * Setup resize event handlers for the resize handle
 * 
 * @param {HTMLElement} resizeHandle - The resize handle element
 * @param {HTMLElement} noteInputCard - The note input card element
 * @param {HTMLElement} referencesCard - The references panel element
 * @param {Function} onResizeComplete - Optional callback when resize is complete
 * @returns {Function} Cleanup function to remove event listeners
 */
export const setupResizeHandlers = (resizeHandle, noteInputCard, referencesCard, onResizeComplete) => {
  let startY = 0;
  let startInputHeight = 0;
  let startReferencesHeight = 0;
  
  const handleMouseDown = (downEvent) => {
    downEvent.preventDefault();
    startY = downEvent.clientY;
    startInputHeight = noteInputCard.offsetHeight;
    startReferencesHeight = referencesCard.offsetHeight;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (moveEvent) => {
    moveEvent.preventDefault();
    const deltaY = moveEvent.clientY - startY;
    
    // Calculate new heights
    const newInputHeight = startInputHeight + deltaY;
    const newReferencesHeight = startReferencesHeight - deltaY;
    
    // Apply minimum heights
    if (newInputHeight >= 100 && newReferencesHeight >= 100) {
      noteInputCard.style.height = `${newInputHeight}px`;
      referencesCard.style.height = `${newReferencesHeight}px`;
      
      // Store the reference height for later use
      referencesCard.dataset.originalHeight = `${newReferencesHeight}px`;
    }
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    
    if (onResizeComplete) {
      onResizeComplete();
    }
  };

  // Add event listener
  resizeHandle.addEventListener('mousedown', handleMouseDown);
  
  // Return cleanup function
  return () => {
    resizeHandle.removeEventListener('mousedown', handleMouseDown);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };
};
